// Third-party
import React from 'react';
import get from 'lodash/get';

// Types
import ComparisonItem from '@/types/comparison-item';
import ComparisonField from '@/types/comparison-field';
import ComparisonCategory from '@/types/comparison-category';
import Comparison from '@/types/comparison';
import RiskPeriods from '@/types/risk-periods';

// Utils
import { formatMessage, formatLocalizedString, getCountryName } from '@/utils/i18n';
import { COMPARISON_ITEM_TYPES, COMPARISON_CATEGORIES } from '@/utils/constants';
import { getCustomDisclaimersForItem } from '@/utils/disclaimers/comparison';
import { DISCLAIMER_TAG } from '@/utils/disclaimers/constants';

// Components
import { Button } from '@/views/components/buttons';
import { TextCell, NotApplicableCell } from '@/views/components/cells';
import { InfoTooltip } from '@/views/components/tooltips';
import { PrimaryLink } from '@/views/components/buttons';

interface FlattenedRow {
  itemId: string;
  itemName: string;
  itemType: string;
  categoryValues: { [categoryKey: string]: { [fieldKey: string]: any } };
  disclaimers?: { [categoryKey: string]: { [fieldKey: string]: string[] } };
}

interface AccessibleTableProps {
  items: ComparisonItem[];
  flattenedData: FlattenedRow[];
  comparison: Comparison;
  categories: ComparisonCategory[];
  locale: any; // Locale type
  riskPeriod: RiskPeriods;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onViewPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  onModifyBenchmark: (item: ComparisonItem) => void;
  onCloneBenchmark: (item: ComparisonItem) => void;
  onItemRemove: (item: ComparisonItem) => void;
  onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  onViewFullDisclaimer?: (item: ComparisonItem) => void;
  readOnly?: boolean;
}

/**
 * Gets the value for a specific field from a comparison item
 * This mirrors the logic in ComparisonValueCell to ensure consistency
 */
const getCellValue = (
  item: ComparisonItem,
  category: ComparisonCategory,
  field: ComparisonField,
  comparison: Comparison,
  locale: any,
  riskPeriod?: RiskPeriods,
): React.ReactNode => {
  // Check if this is a risk metric field (has multiValue flag)
  const isRiskField = field.multiValue;

  if (isRiskField && riskPeriod) {
    // For risk fields, get the value using the risk period
    const value = get(item, `${field.key}.${riskPeriod}`, null);
    return renderCellValue(value, field, category, item, comparison, locale, riskPeriod);
  }
  // For non-risk fields, get the value directly
  const value = get(item, field.key, null);
  return renderCellValue(value, field, category, item, comparison, locale);
};

/**
 * Processes the value like ComparisonValueCell does, including special cases
 */
const processValue = (value: any, field: ComparisonField, category: ComparisonCategory): any => {
  if (!value) return value;

  // Apply the same value processing as ComparisonValueCell
  if (category.key === COMPARISON_CATEGORIES.INDUSTRY_HOLDINGS && value) {
    return { ...value, category: COMPARISON_CATEGORIES.INDUSTRY_HOLDINGS };
  }

  if (category.key === COMPARISON_CATEGORIES.COUNTRY_ALLOCATIONS && value) {
    return { ...value, category: COMPARISON_CATEGORIES.COUNTRY_ALLOCATIONS };
  }

  if (category.key === COMPARISON_CATEGORIES.KEY_METRICS && value) {
    switch (field.key) {
      case 'domicile':
        return getCountryName(value);
      case 'classification':
        return formatLocalizedString(value);
      default:
        return value;
    }
  }

  return value;
};

/**
 * Renders a cell value using the same cell component as the main comparison table
 */
const renderCellValue = (
  value: any,
  field: ComparisonField,
  category: ComparisonCategory,
  item: ComparisonItem,
  comparison: Comparison,
  locale: any,
  riskPeriod?: RiskPeriods,
): React.ReactNode => {
  // Check for itemType restrictions (same logic as ComparisonValueCell)
  if ((field.itemTypes && !field.itemTypes.includes(item.type)) || (category.itemTypes && !category.itemTypes.includes(item.type))) {
    return React.createElement(NotApplicableCell, {
      hideTooltip: false,
      className: 'accessible-comparison-cell not-applicable',
      locale,
    });
  }

  const processedValue = processValue(value, field, category);

  // Use the same cell component as defined in the field configuration
  const CellComponent = field.cell || TextCell;

  // Create props similar to ComparisonValueCell
  const cellProps = {
    value: processedValue,
    className: `${field.className || ''} accessible-comparison-cell`,
    checkboxOptions: field.checkboxOptions,
    item,
    riskPeriod,
    comparison,
    locale,
    // Don't include some interactive props but keep benchmarks modal functional
    onClick: undefined,
    onShowCorrelation: undefined,
    linkDisabled: false, // Keep benchmarks modal functional for accessibility
  };

  try {
    return React.createElement(CellComponent, cellProps);
  } catch (error) {
    console.warn(`Error rendering cell for field ${field.key}:`, error);
    // Fallback to NotApplicableCell if rendering fails
    return React.createElement(NotApplicableCell, {
      hideTooltip: false,
      className: 'accessible-comparison-cell not-applicable',
      locale,
    });
  }
};

/**
 * Formats a value for display in the table based on the field type and data structure
 */
const formatValue = (value: any, fieldKey: string): string => {
  if (value === null || value === undefined) {
    return '--';
  }

  // Handle arrays (like top holdings)
  if (Array.isArray(value)) {
    if (value.length === 0) return '--';

    // Handle holdings arrays
    if (value[0] && typeof value[0] === 'object' && value[0].name) {
      return value
        .slice(0, 5) // Show max 5 items
        .map(item => {
          if (item.weight !== undefined) {
            return `${item.name} (${(item.weight * 100).toFixed(1)}%)`;
          }
          return item.name;
        })
        .join(', ');
    }

    // Handle other arrays
    return value.slice(0, 3).join(', ') + (value.length > 3 ? '...' : '');
  }

  // Handle objects (like individual holdings, allocations, etc.)
  if (typeof value === 'object' && value !== null) {
    // Handle holding objects
    if (value.name && value.weight !== undefined) {
      return `${value.name} (${(value.weight * 100).toFixed(1)}%)`;
    }

    // Handle objects with just name
    if (value.name) {
      return value.name;
    }

    // Handle date objects
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }

    // Handle objects with value property
    if (value.value !== undefined) {
      return formatValue(value.value, fieldKey);
    }

    // Fallback for complex objects - try to find meaningful properties
    if (value.label) return value.label;
    if (value.text) return value.text;
    if (value.description) return value.description;

    return JSON.stringify(value);
  }

  // Handle numbers
  if (typeof value === 'number') {
    // Handle percentage fields
    if (
      fieldKey.includes('percent') ||
      fieldKey.includes('allocation') ||
      fieldKey.includes('mer') ||
      fieldKey.includes('yield') ||
      fieldKey.includes('ratio') ||
      fieldKey.includes('weight')
    ) {
      return `${(value * 100).toFixed(2)}%`;
    }

    // Handle currency fields
    if (fieldKey.includes('investment') || fieldKey.includes('assets') || fieldKey.includes('tna') || fieldKey.includes('value') || fieldKey.includes('fee')) {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
      }).format(value);
    }

    // Handle growth/performance fields (already in percentage form)
    if (fieldKey.includes('growth') || fieldKey.includes('performance')) {
      return `${value.toFixed(2)}%`;
    }

    // Default number formatting
    return value.toFixed(2);
  }

  // Handle strings
  if (typeof value === 'string') {
    return value;
  }

  // Handle dates
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Fallback for any other type
  return String(value);
};

/**
 * Gets available actions for an item (excluding "Extend portfolio")
 */
const getItemActions = (
  item: ComparisonItem,
  readOnly: boolean | undefined,
  callbacks: {
    onModifyPortfolio: (item: ComparisonItem) => void;
    onViewPortfolio: (item: ComparisonItem) => void;
    onClonePortfolio: (item: ComparisonItem) => void;
    onModifyBenchmark: (item: ComparisonItem) => void;
    onCloneBenchmark: (item: ComparisonItem) => void;
    onItemRemove: (item: ComparisonItem) => void;
    onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  },
) => {
  const actions = [];

  if (!readOnly) {
    if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
      actions.push({
        label: formatMessage('Comparison.ItemMenu.ModifyPortfolio'),
        onClick: () => callbacks.onModifyPortfolio(item),
      });
      actions.push({
        label: formatMessage('Comparison.ItemMenu.ClonePortfolio'),
        onClick: () => callbacks.onClonePortfolio(item),
      });
      actions.push({
        label: formatMessage('Comparison.ItemMenu.SaveToContact'),
        onClick: () => callbacks.onSavePortfolio(item),
      });
    } else if (item.type === COMPARISON_ITEM_TYPES.BENCHMARK) {
      actions.push({
        label: formatMessage('Comparison.ItemMenu.Benchmark.Modify'),
        onClick: () => callbacks.onModifyBenchmark(item),
      });
      actions.push({
        label: formatMessage('Comparison.ItemMenu.CloneBenchmark'),
        onClick: () => callbacks.onCloneBenchmark(item),
      });
    }

    actions.push({
      label: formatMessage('Comparison.ItemMenu.Remove'),
      onClick: () => callbacks.onItemRemove(item),
    });
  } else {
    actions.push({
      label: formatMessage('Comparison.ItemMenu.ViewPortfolio'),
      onClick: () => callbacks.onViewPortfolio(item),
    });
  }

  return actions;
};

const AccessibleTable: React.FC<AccessibleTableProps> = ({
  items,
  flattenedData,
  comparison,
  categories,
  locale,
  riskPeriod,
  readOnly,
  onModifyPortfolio,
  onViewPortfolio,
  onClonePortfolio,
  onModifyBenchmark,
  onCloneBenchmark,
  onItemRemove,
  onSavePortfolio,
  onViewFullDisclaimer,
}) => {
  if (flattenedData.length === 0) {
    return (
      <div className="accessible-table-empty">
        <p>{formatMessage('AccessibleTable.NoData')}</p>
      </div>
    );
  }

  const callbacks = {
    onModifyPortfolio,
    onViewPortfolio,
    onClonePortfolio,
    onModifyBenchmark,
    onCloneBenchmark,
    onItemRemove,
    onSavePortfolio,
  };

  return (
    <div className="accessible-comparison-table-container">
      <table className="accessible-comparison-table" role="table">
        <caption className="visually-hidden">
          {formatMessage('AccessibleTable.Caption', { itemCount: items.length })}
          {formatMessage('AccessibleTable.TableStructure')}
        </caption>

        {/* Item column */}
        <col />
        {/* Actions column */}
        <col />
        {/* Category column groups */}
        {categories.map(category => {
          const isCalendarYearCategory = category.key === COMPARISON_CATEGORIES.CALENDAR_YEAR_RETURNS;
          const regularFields = category.fields.filter(field => !field.multiValue || isCalendarYearCategory);
          const multiValueFields = category.fields.filter(field => field.multiValue && !isCalendarYearCategory);

          // Calculate total columns for this category
          const multiValueFieldCount = multiValueFields.reduce((count, field) => {
            const shouldExpandByRiskPeriod = field.key !== 'performance_correlation';
            if (shouldExpandByRiskPeriod) {
              return count + 7; // 7 risk periods: YT, 1Y, 3Y, 5Y, 10Y, INC, CI
            }
            return count + 1; // Just one column for correlation
          }, 0);

          const totalFieldCount = regularFields.length + multiValueFieldCount;

          // Use colgroup with span for grouped category columns
          return <colgroup key={`${category.key}-colgroup`} span={totalFieldCount} />;
        })}

        <thead>
          <tr>
            <th id="header-item" rowSpan={2} scope="col">
              {formatMessage('AccessibleTable.Item')}
            </th>
            <th id="header-actions" rowSpan={2} scope="col">
              {formatMessage('AccessibleTable.Actions')}
            </th>
            {categories.map(category => {
              // Calculate total fields for this category (including expanded risk period fields)
              const isCalendarYearCategory = category.key === COMPARISON_CATEGORIES.CALENDAR_YEAR_RETURNS;
              const regularFields = category.fields.filter(field => !field.multiValue || isCalendarYearCategory);
              const multiValueFields = category.fields.filter(field => field.multiValue && !isCalendarYearCategory);

              // For multiValue fields, count each risk period as a separate column
              const multiValueFieldCount = multiValueFields.reduce((count, field) => {
                const shouldExpandByRiskPeriod = field.key !== 'performance_correlation';
                if (shouldExpandByRiskPeriod) {
                  return count + 7; // 7 risk periods: YT, 1Y, 3Y, 5Y, 10Y, INC, CI
                }
                return count + 1; // Just one column for correlation
              }, 0);

              const totalFieldCount = regularFields.length + multiValueFieldCount;

              return (
                <th key={category.key} id={`header-${category.key}`} scope="colgroup" colSpan={totalFieldCount}>
                  {category.label}
                </th>
              );
            })}
          </tr>
          <tr>
            {categories.map(category => {
              const isCalendarYearCategory = category.key === COMPARISON_CATEGORIES.CALENDAR_YEAR_RETURNS;

              return (
                <React.Fragment key={`${category.key}-fields`}>
                  {/* Regular fields (non-multiValue or calendar year fields) */}
                  {category.fields
                    .filter(field => !field.multiValue || isCalendarYearCategory)
                    .map(field => (
                      <th key={`${category.key}-${field.key}`} id={`header-${category.key}-${field.key}`} headers={`header-${category.key}`} scope="col">
                        {field.label || field.key}
                      </th>
                    ))}

                  {/* MultiValue fields with risk periods (excluding calendar year fields) */}
                  {!isCalendarYearCategory &&
                    category.fields
                      .filter(field => field.multiValue)
                      .map(field => {
                        const shouldExpandByRiskPeriod = field.key !== 'performance_correlation';

                        if (shouldExpandByRiskPeriod) {
                          const riskPeriods: RiskPeriods[] = ['YT', '1Y', '3Y', '5Y', '10Y', 'INC', 'CI'];
                          return riskPeriods.map(period => (
                            <th
                              key={`${category.key}-${field.key}-${period}`}
                              id={`header-${category.key}-${field.key}-${period}`}
                              headers={`header-${category.key}`}
                              scope="col"
                            >
                              {field.label || field.key} {period}
                            </th>
                          ));
                        }
                        return (
                          <th key={`${category.key}-${field.key}`} id={`header-${category.key}-${field.key}`} headers={`header-${category.key}`} scope="col">
                            {field.label || field.key}
                          </th>
                        );
                      })}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {flattenedData.map(row => {
            const item = items.find(i => i._id === row.itemId);
            if (!item) return null;

            return (
              <tr key={row.itemId}>
                <th id={`item-${row.itemId}`} scope="row" headers="header-item">
                  <div className="item-info">
                    <strong>{row.itemName}</strong>
                    {/* Add View full disclaimer link for items with custom disclaimers */}
                    {onViewFullDisclaimer && getCustomDisclaimersForItem(item, [DISCLAIMER_TAG.COMPARISON]).length > 0 && (
                      <div className="item-disclaimer-link">
                        <PrimaryLink onClick={() => onViewFullDisclaimer(item)}>
                          <i className="icon-ds-info" />
                          {formatMessage('Comparison.CustomFundDisclaimer.Title')}
                        </PrimaryLink>
                      </div>
                    )}
                  </div>
                </th>

                <td headers={`header-actions item-${row.itemId}`}>
                  <div className="item-actions">
                    {getItemActions(item, readOnly, callbacks).map((action, index) => (
                      <Button key={index} compact noBackground onClick={action.onClick} className="item-action-button">
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </td>

                {categories.map(category => {
                  const categoryData = row.categoryValues[category.key] || {};
                  const isCalendarYearCategory = category.key === COMPARISON_CATEGORIES.CALENDAR_YEAR_RETURNS;

                  return (
                    <React.Fragment key={`${category.key}-data`}>
                      {/* Regular fields (non-multiValue or calendar year fields) */}
                      {category.fields
                        .filter(field => !field.multiValue || isCalendarYearCategory)
                        .map(field => {
                          const value = categoryData[field.key];
                          const fieldDisclaimers = row.disclaimers?.[category.key]?.[field.key] || [];
                          const headerId = `header-${category.key}-${field.key}`;

                          return (
                            <td key={`${category.key}-${field.key}`} headers={`${headerId} header-${category.key} item-${row.itemId}`}>
                              <div className="field-content">
                                {renderCellValue(value, field, category, item, comparison, locale, riskPeriod)}
                                {fieldDisclaimers.length > 0 && (
                                  <div className="field-disclaimers">
                                    {fieldDisclaimers.map((disclaimer, index) => (
                                      <small key={index} className="disclaimer">
                                        {disclaimer}
                                      </small>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}

                      {/* MultiValue fields with risk periods (excluding calendar year fields) */}
                      {!isCalendarYearCategory &&
                        category.fields
                          .filter(field => field.multiValue)
                          .map(field => {
                            const shouldExpandByRiskPeriod = field.key !== 'performance_correlation';

                            if (shouldExpandByRiskPeriod) {
                              const riskPeriods: RiskPeriods[] = ['YT', '1Y', '3Y', '5Y', '10Y', 'INC', 'CI'];
                              return riskPeriods.map(period => {
                                const fieldKeyWithPeriod = `${field.key}_${period}`;
                                const value = categoryData[fieldKeyWithPeriod];
                                const fieldDisclaimers = row.disclaimers?.[category.key]?.[fieldKeyWithPeriod] || [];
                                const headerId = `header-${category.key}-${field.key}-${period}`;

                                return (
                                  <td key={`${category.key}-${field.key}-${period}`} headers={`${headerId} header-${category.key} item-${row.itemId}`}>
                                    <div className="field-content">
                                      {renderCellValue(value, field, category, item, comparison, locale, period)}
                                      {fieldDisclaimers.length > 0 && (
                                        <div className="field-disclaimers">
                                          {fieldDisclaimers.map((disclaimer, index) => (
                                            <small key={index} className="disclaimer">
                                              {disclaimer}
                                            </small>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                );
                              });
                            }
                            // For correlation and other special multiValue fields, show only once
                            const value = categoryData[field.key];
                            const fieldDisclaimers = row.disclaimers?.[category.key]?.[field.key] || [];
                            const headerId = `header-${category.key}-${field.key}`;

                            return (
                              <td key={`${category.key}-${field.key}`} headers={`${headerId} header-${category.key} item-${row.itemId}`}>
                                <div className="field-content">
                                  {renderCellValue(value, field, category, item, comparison, locale, riskPeriod)}
                                  {fieldDisclaimers.length > 0 && (
                                    <div className="field-disclaimers">
                                      {fieldDisclaimers.map((disclaimer, index) => (
                                        <small key={index} className="disclaimer">
                                          {disclaimer}
                                        </small>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AccessibleTable;
